<template>
  <Transition name="fag">
    <div
      v-if="open"
      class="fag"
      :class="{ 'fag--fixed': fixed }"
      role="alertdialog"
      :aria-labelledby="`${testidPrefix}-abandon-title`"
      :data-testid="`${testidPrefix}-abandon-guard`"
    >
      <div class="fag-box">
        <h3 :id="`${testidPrefix}-abandon-title`" class="fag-title">{{ title }}</h3>
        <p class="fag-text">{{ text }}</p>
        <div class="fag-actions">
          <Button
            variant="secondary"
            :data-testid="`${testidPrefix}-abandon-stay`"
            @click="$emit('continue')"
          >
            {{ stayLabel }}
          </Button>
          <Button
            variant="danger"
            :data-testid="`${testidPrefix}-abandon-leave`"
            @click="$emit('abandon')"
          >
            {{ leaveLabel }}
          </Button>
        </div>
      </div>
    </div>
  </Transition>
</template>

<script setup>
/**
 * GARDE D'ABANDON D'UN FORMULAIRE COMMENCÉ.
 *
 * Extrait de `quote-besoin/BesoinAbandonGuard.vue`. Une saisie longue ne se
 * jette pas sur un Échap ni sur un clic dans le menu. `DialogForm` rend cette
 * garde aux fenêtres ordinaires ; une page n'est pas une fenêtre du tout —
 * elle la reprend donc ici.
 *
 * `fixed` dit d'où elle est posée : dans une fenêtre elle couvre la fenêtre
 * (l'hôte porte déjà `position: relative`) ; sur une page elle couvre l'écran.
 *
 * PRÉSENTATIONNEL : `open` entre, `continue` et `abandon` sortent.
 */
import Button from '../Button.vue'

defineProps({
  open: {
    type: Boolean,
    default: false
  },
  /** Vrai sur une page : la garde couvre l'écran et non un cadre parent. */
  fixed: {
    type: Boolean,
    default: false
  },
  title: {
    type: String,
    default: 'Abandonner la saisie en cours ?'
  },
  text: {
    type: String,
    default: 'Vous avez commencé à remplir ce formulaire sans l’enregistrer.'
  },
  stayLabel: {
    type: String,
    default: 'Continuer la saisie'
  },
  leaveLabel: {
    type: String,
    default: 'Quitter sans enregistrer'
  },
  /** Préfixe des `data-testid` (`<prefix>-abandon-guard`, `-stay`, `-leave`). */
  testidPrefix: {
    type: String,
    default: 'form'
  }
})

defineEmits(['continue', 'abandon'])
</script>

<style scoped>
.fag {
  position: absolute;
  inset: 0;
  z-index: 10;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: var(--spacing-6);
  background: rgba(15, 23, 42, 0.45);
}

.fag--fixed {
  position: fixed;
  z-index: var(--z-modal, 1050);
}

.fag-box {
  width: 100%;
  max-width: 420px;
  padding: var(--spacing-6);
  border-radius: var(--radius-lg);
  background: var(--color-bg-primary);
  box-shadow: var(--shadow-xl);
  display: flex;
  flex-direction: column;
  gap: var(--spacing-3);
}

.fag-title {
  margin: 0;
  padding: 0;
  border: none;
  font-size: var(--font-size-lg);
  font-weight: var(--font-weight-semibold);
  color: var(--color-text-primary);
}

.fag-text {
  margin: 0;
  font-size: var(--font-size-sm);
  line-height: var(--line-height-normal);
  color: var(--color-text-secondary);
}

.fag-actions {
  display: flex;
  justify-content: flex-end;
  gap: var(--spacing-3);
  flex-wrap: wrap;
}

.fag-enter-active,
.fag-leave-active {
  transition: opacity 0.18s ease;
}

.fag-enter-from,
.fag-leave-to {
  opacity: 0;
}
</style>
